/**
 * Administration → Storage → Integrity check (1.0.0-rc.3): checks that every file the catalog lists is in storage
 * with the recorded size (and, deep, checksum), and lists files nothing references. It runs as a background job on
 * the server with its progress shown here, so it can take as long as a large archive needs; the page can be left
 * and opened again.
 *
 * 1.0.0-rc.14: "Not in the catalog" is what Temporary files would list, shown in full (up to the report's 200), with
 * where to remove them. The deep check reads only server folders, and asks first: it is a heavy operation.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleCheck, CircleX, ShieldCheck, Square } from 'lucide-react';
import { api, errorDetail } from '../../../api/client';
import { ProgressLine } from '../../../components/JobProgress';
import { Modal } from '../../../components/Modal';
import { TableScroll } from '../../../components/TableScroll';
import { toast } from '../../../components/Toasts';
import { bytes, dateTime, relative } from '../../../lib/format';

/** Reports from before rc.14 have no kind, size field, date or `unreadable` problems: they still render. */
interface Problem {
  location: string; key: string; problem: 'missing' | 'size_mismatch' | 'checksum_mismatch' | 'unreadable' | 'unreferenced'; expected?: string | number; actual?: string | number;
  assetId?: string | null; kind?: string; sizeBytes?: number; modifiedAt?: string; message?: string;
}
interface Report {
  ok: boolean; deep: boolean;
  locations: Array<{ id: string; name: string; checked: number; missing: number; sizeMismatch: number; checksumMismatch: number; checksummed: number; unreadable?: number; unreferenced: number; unreachable: string | null }>;
  problems?: Problem[]; problemCount?: number; unreferencedLimit?: number; unreferencedTruncated?: boolean;
}
export interface IntegrityJob {
  id: string; status: 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled'; deep: boolean; checked: number; total: number | null; percent: number | null;
  currentLocation: string | null; report: Report | null; error: string | null; requestedBy: string | null; createdAt: string; finishedAt: string | null;
}

const PROBLEM_TEXT: Record<Problem['problem'], string> = { missing: 'Missing', size_mismatch: 'Wrong size', checksum_mismatch: 'Checksum differs', unreadable: 'Could not be read', unreferenced: 'Not in the catalog' };
/** Where leftover files are removed (Administration → System status → Housekeeping → Temporary files). */
const TEMP_FILES_HREF = '/admin/status#temp-files';
const DEEP_CHECK_DISCLAIMER = "A deep check reads up to 1,000 files from the server's storage folders in full to compare their checksums. It can take hours on a large archive and slows other work on the server while it runs. Run it only when you need it, for example after restoring a backup or when you suspect disk damage. Object storage and archive tiers are not read.";
/** Full keys, wrapped: the table's cells otherwise cut them short. */
const KEY_CELL = { whiteSpace: 'normal', overflowWrap: 'anywhere', minWidth: 200, height: 'auto', fontSize: 12 } as const;
const sizeOf = (p: Problem) => p.sizeBytes ?? (typeof p.actual === 'number' ? p.actual : null);
const active = (j?: IntegrityJob | null) => j?.status === 'queued' || j?.status === 'running';

export function IntegrityCheck() {
  const qc = useQueryClient();
  const [starting, setStarting] = useState(false);
  const [confirmDeep, setConfirmDeep] = useState(false);
  const list = useQuery({
    queryKey: ['integrity-jobs'],
    queryFn: () => api<{ items: IntegrityJob[]; deepEstimate?: { files: number; bytes: number; limit: number } }>('/api/v1/operations/integrity/jobs'),
    refetchInterval: (q) => (active(q.state.data?.items[0]) ? 1000 : false),
  });
  const latest = list.data?.items[0] ?? null;
  const estimate = list.data?.deepEstimate ?? null;
  // The finished job's problems come with the job itself.
  const detail = useQuery({
    queryKey: ['integrity-job', latest?.id, latest?.status],
    queryFn: () => api<IntegrityJob>(`/api/v1/operations/integrity/jobs/${latest!.id}`),
    enabled: Boolean(latest && latest.status === 'succeeded'),
  });

  async function start(deep: boolean) {
    setStarting(true);
    try {
      await api('/api/v1/operations/integrity/jobs', { method: 'POST', json: { deep } });
      await qc.invalidateQueries({ queryKey: ['integrity-jobs'] });
    } catch (err) {
      toast(errorDetail(err), 'error');
    } finally {
      setStarting(false);
    }
  }
  async function cancel() {
    if (!latest) return;
    try {
      await api(`/api/v1/operations/integrity/jobs/${latest.id}/cancel`, { method: 'POST', json: {} });
      await qc.invalidateQueries({ queryKey: ['integrity-jobs'] });
    } catch (err) {
      toast(errorDetail(err), 'error');
    }
  }

  const report = detail.data?.report ?? null;
  const problems = (report?.problems ?? []).filter((p) => p.problem !== 'unreferenced');
  const unreferenced = (report?.problems ?? []).filter((p) => p.problem === 'unreferenced');
  const unreferencedTotal = (report?.locations ?? []).reduce((n, l) => n + l.unreferenced, 0);
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginTop: 32 }}>
        <h2 style={{ margin: 0 }}>Integrity check</h2>
      </div>
      <p className="lede">Checks that every file the catalog lists is in its storage with the recorded size, and lists the files in PRISM&apos;s folders on storage locations that nothing refers to (the same files Temporary files finds). A deep check also compares checksums, reading files on the server&apos;s storage folders only. Use it after restoring a backup. It runs on the server; you can leave this page.</p>
      <div className="panel-card" style={{ maxWidth: 980 }} data-testid="integrity-check">
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button className="btn primary" disabled={starting || active(latest)} onClick={() => void start(false)} data-testid="integrity-start"><ShieldCheck size={14} /> Check now</button>
          <button className="btn" disabled={starting || active(latest)} onClick={() => setConfirmDeep(true)} data-testid="integrity-start-deep"
            title="Also reads up to 1,000 files on the server's storage folders in full to compare checksums; heavy on large archives. Object storage and archive tiers are checked for size only">Deep check (server folders)</button>
          {active(latest) && <button className="btn" onClick={() => void cancel()} data-testid="integrity-cancel"><Square size={13} /> Stop</button>}
          <span style={{ flex: 1 }} />
          {latest && <span className="faint" style={{ fontSize: 12 }}>{latest.deep ? 'Deep check' : 'Check'} started {relative(latest.createdAt)}{latest.requestedBy ? ` by ${latest.requestedBy}` : ''}</span>}
        </div>
        {active(latest) && (
          <div data-testid="integrity-progress" data-status={latest!.status}>
            <ProgressLine step={latest!.currentLocation ? `Checking ${latest!.currentLocation}` : 'Starting'} percent={latest!.percent} />
            <span className="faint" style={{ fontSize: 12 }}>{latest!.checked.toLocaleString()} of {latest!.total == null ? '…' : latest!.total.toLocaleString()} files</span>
          </div>
        )}
        {latest?.status === 'failed' && <p className="error-text" data-testid="integrity-error"><CircleX size={14} /> {latest.error}</p>}
        {latest?.status === 'cancelled' && <p className="faint">Stopped after {latest.checked.toLocaleString()} files.</p>}
        {latest?.status === 'succeeded' && report && (
          <div data-testid="integrity-result" data-ok={report.ok}>
            <p style={{ margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
              {report.ok ? <CircleCheck size={15} className="ok-text" /> : <CircleX size={15} className="error-text" />}
              <b>{report.ok ? 'Every catalogued file is in place' : `${problems.length} problem${problems.length === 1 ? '' : 's'} found`}</b>
              <span className="faint">· finished {relative(latest.finishedAt)}</span>
            </p>
            <TableScroll>
            <table className="table compact">
              <thead><tr><th>Location</th><th>Checked</th><th>Missing</th><th>Wrong size</th><th>Checksums</th><th>Not in the catalog</th></tr></thead>
              <tbody>{report.locations.map((l) => (
                <tr key={l.id}><td className="wrap">{l.name}{l.unreachable ? <span className="error-text" title={l.unreachable}> · unreachable</span> : null}</td><td>{l.checked}</td><td>{l.missing}</td><td>{l.sizeMismatch}</td>
                  <td className="wrap">{report.deep ? `${l.checksummed} checked, ${l.checksumMismatch} differ${l.unreadable ? `, ${l.unreadable} unreadable` : ''}` : '—'}</td><td>{l.unreferenced}</td></tr>
              ))}</tbody>
            </table>
            </TableScroll>
            {problems.length > 0 && (
              <TableScroll>
              <table className="table compact" style={{ marginTop: 12 }} data-testid="integrity-problems">
                <thead><tr><th>Problem</th><th>Location</th><th>File</th><th>Asset</th></tr></thead>
                <tbody>{problems.slice(0, 100).map((p) => (
                  <tr key={`${p.location}:${p.key}`}><td className="wrap">{PROBLEM_TEXT[p.problem] ?? p.problem}</td><td className="wrap">{p.location}</td>
                    <td className="mono" title={p.key} style={KEY_CELL}>{p.key}{p.message ? <div className="faint" style={{ fontFamily: 'inherit', fontSize: 12 }}>{p.message}</div> : null}</td>
                    <td>{p.assetId ? <a href={`/assets/${p.assetId}`}>Open</a> : '—'}</td></tr>
                ))}</tbody>
              </table>
              </TableScroll>
            )}
            {unreferenced.length > 0 && (
              <div style={{ marginTop: 16 }} data-testid="integrity-unreferenced">
                <h3 style={{ margin: '0 0 4px', fontSize: 14 }}>Not in the catalog</h3>
                <p className="faint" style={{ margin: '0 0 8px', fontSize: 12 }}>
                  Files in PRISM&apos;s folders, over a day old, that nothing in PRISM refers to; they may have been written after the backup was taken, or left by work that stopped.
                  An administrator can remove them under <Link to={TEMP_FILES_HREF} style={{ color: 'var(--accent-strong)' }} data-testid="integrity-temp-files-link">Administration → System status → Housekeeping → Temporary files</Link>.
                </p>
                <TableScroll>
                <table className="table compact">
                  <thead><tr><th>Location</th><th>File</th><th>Kind</th><th style={{ textAlign: 'right' }}>Size</th><th>Changed</th></tr></thead>
                  <tbody>{unreferenced.map((p) => (
                    <tr key={`${p.location}:${p.key}`} data-testid="integrity-unreferenced-row"><td className="wrap">{p.location}</td><td className="mono" title={p.key} style={KEY_CELL}>{p.key}</td><td>{p.kind ?? '—'}</td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{sizeOf(p) == null ? '—' : bytes(sizeOf(p))}</td><td style={{ whiteSpace: 'nowrap' }}>{p.modifiedAt ? dateTime(p.modifiedAt) : '—'}</td></tr>
                  ))}</tbody>
                </table>
                </TableScroll>
                {(report.unreferencedTruncated || (report.unreferencedLimit == null && unreferencedTotal > unreferenced.length)) && (
                  <p className="faint" style={{ marginTop: 6, fontSize: 12 }} data-testid="integrity-unreferenced-truncated">
                    The report lists the first {unreferenced.length.toLocaleString()} of {unreferencedTotal.toLocaleString()} files; Temporary files lists them all.
                  </p>
                )}
              </div>
            )}
          </div>
        )}
        {!latest && !list.isLoading && <p className="faint">No check has run yet.</p>}
      </div>
      {confirmDeep && (
        <Modal title="Start a deep check?" onClose={() => setConfirmDeep(false)} actions={<>
          <button className="btn ghost" onClick={() => setConfirmDeep(false)} data-testid="integrity-deep-cancel">Cancel</button>
          <button className="btn primary" disabled={starting} data-testid="integrity-deep-confirm" onClick={() => { setConfirmDeep(false); void start(true); }}>Start deep check</button>
        </>}>
          <p style={{ marginTop: 0 }} data-testid="integrity-deep-disclaimer">{DEEP_CHECK_DISCLAIMER}</p>
          {estimate && (
            <p className="faint" style={{ marginBottom: 0 }} data-testid="integrity-deep-estimate">
              {estimate.files === 0 ? "No catalogued file on the server's storage folders has a checksum to compare."
                : `This check reads ${estimate.files >= estimate.limit ? 'up to ' : ''}${estimate.files.toLocaleString()} file${estimate.files === 1 ? '' : 's'}, about ${bytes(estimate.bytes)}.`}
            </p>
          )}
        </Modal>
      )}
    </>
  );
}
